import { hot } from "react-hot-loader/root";
import React, { useState, useEffect } from "react";
import Section from "../components/Section";
import {
  Button,
  Column,
  Columns,
  Pagination,
  Panel,
  Select,
  Table,
  TextField,
  Tooltip,
} from "@sparkpost/matchbox";
import { i18n } from "../utils/utils";
import { getLogs } from "../utils/ajax";
import { useAppState } from "../utils/context";
import { ExpandLess, ExpandMore, Refresh } from "@sparkpost/matchbox-icons";

const Logs = () => {
  const [dateRange, setDateRange] = useState("last_30days");
  const [resetExpand, setResetExpand] = useState(false);
  const { updateField, fields, isLoading, setLoading, displayNotice } =
    useAppState();

  /**
   * Main function in charge of fetching logs via AJAX.
   */
  const fetchLogs = async (page = 1, force = false) => {
    if (isLoading && !force) {
      return;
    }

    setLoading(true);
    setResetExpand(true);

    const result = await getLogs(
      {
        status: fields.logsFilters.status || "all",
        recipient: fields.logsFilters.recipient || "",
        date_range: dateRange,
      },
      page
    );

    if (result.success === true) {
      updateField("logs", result.fields.logs.data);
      updateField("logsPagination", result.fields.logs.pagination);
    } else {
      updateField("logs", []);
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
    setResetExpand(false);
  };

  /**
   * Update a filter.
   */
  const changeFilter = (name, value) => {
    updateField("logsFilters", {
      ...fields.logsFilters,
      [name]: value,
    });
  };

  /**
   * Update date range filter.
   */
  const changeDateRange = (range) => {
    setDateRange(range);
  };

  /**
   * On first component load, fetch logs.
   */
  useEffect(() => {
    fetchLogs(1, true);
  }, []);

  /**
   * When date range changes, fetch logs.
   */
  useEffect(() => {
    fetchLogs(1, true);
  }, [dateRange]);

  /**
   * When clicking on a filter.
   */
  const onFilterClick = async (e) => {
    e.preventDefault();
    updateField("logsPage", 1);
    fetchLogs(1);
  };

  /**
   * When clicking on a pagination.
   */
  const onPaginationChange = (i) => {
    const newPage = parseInt(i + 1);

    if (newPage === fields.logsPage) {
      return;
    }

    updateField("logsPage", newPage);
    fetchLogs(newPage, true);
  };

  const hasLogs = typeof fields.logs !== "undefined";

  return (
    <div className="section-logs">
      <Section>
        <form onSubmit={onFilterClick}>
          <Columns>
            <Column width={2 / 5}>
              <Select
                label={i18n("Fields.Logs.FilterByStatus")}
                onChange={(e) => changeFilter("status", e.target.value)}
                options={[
                  {
                    value: "all",
                    label: i18n("Fields.Logs.StatusAll"),
                  },
                  {
                    value: "pending",
                    label: i18n("Fields.Logs.StatusPending"),
                  },
                  {
                    value: "delivered",
                    label: i18n("Fields.Logs.StatusDelivered"),
                  },
                  {
                    value: "failed",
                    label: i18n("Fields.Logs.StatusFailed"),
                  },
                  {
                    value: "error",
                    label: i18n("Fields.Logs.StatusError"),
                  },
                ]}
              />
            </Column>
            <Column width={2 / 5}>
              <TextField
                id="filter_recipient"
                label={i18n("Fields.Logs.FilterByRecipient")}
                placeholder="someone@email.com"
                value={fields.logsFilters.recipient || ""}
                onChange={(e) => changeFilter("recipient", e.target.value)}
              />
            </Column>
            <Column width={1 / 5} className="align-bottom">
              <Button
                size="default"
                width="100%"
                variant="outline"
                color="blue"
                onClick={onFilterClick}
                mutedOutline
              >
                {i18n("Button.Filter")}
              </Button>
            </Column>
          </Columns>
        </form>
      </Section>
      <Section className="date-range-section">
        <nav className="date-range-selectors">
          <ul className="range-selector">
            <li
              className={`${dateRange === "last_hour" ? "active" : ""}`}
              onClick={() => changeDateRange("last_hour")}
            >
              {i18n("Fields.Logs.LastHour")}
            </li>
            <li
              className={`${dateRange === "last_24hours" ? "active" : ""}`}
              onClick={() => changeDateRange("last_24hours")}
            >
              {i18n("Fields.Logs.Last24Hours")}
            </li>
            <li
              className={`${dateRange === "last_7days" ? "active" : ""}`}
              onClick={() => changeDateRange("last_7days")}
            >
              {i18n("Fields.Logs.Last7Days")}
            </li>
            <li
              className={`${dateRange === "last_30days" ? "active" : ""}`}
              onClick={() => changeDateRange("last_30days")}
            >
              {i18n("Fields.Logs.Last30Days")}
            </li>
          </ul>

          <Button
            size="small"
            variant="text"
            color="blue"
            onClick={onFilterClick}
            mutedOutline
            className="refresh-button"
          >
            <Refresh size="36" fill="gray" />
          </Button>
        </nav>
      </Section>
      {hasLogs && (
        <>
          <LogsTablenavigator
            currentPage={fields.logsPage}
            pagination={fields.logsPagination || {}}
            onPaginationChange={onPaginationChange}
          />
          <LogsTable logs={fields.logs} resetExpand={resetExpand} />
          <LogsTablenavigator
            currentPage={fields.logsPage}
            pagination={fields.logsPagination || {}}
            onPaginationChange={onPaginationChange}
          />
        </>
      )}
    </div>
  );
};

const LogsTable = ({ logs, resetExpand }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const expandRow = (index) => {
    setExpandedRow(index);
  };

  useEffect(() => {
    if (resetExpand) {
      setExpandedRow(null);
    }
  }, [resetExpand]);

  return (
    <Panel mt="300">
      <Table className="logs-table">
        <thead>
          <Table.Row header>
            <Table.HeaderCell className="col-date">
              {i18n("Text.Date")}
            </Table.HeaderCell>
            <Table.HeaderCell className="col-subject">
              {i18n("Text.Subject")}
            </Table.HeaderCell>
            <Table.HeaderCell className="col-sender">
              {i18n("Text.Sender")}
            </Table.HeaderCell>
            <Table.HeaderCell className="col-recipient">
              {i18n("Text.Recipient")}
            </Table.HeaderCell>
            <Table.HeaderCell className="col-status">
              {i18n("Text.Status")}
            </Table.HeaderCell>
            <Table.HeaderCell className="col-actions"> </Table.HeaderCell>
          </Table.Row>
        </thead>
        <tbody>
          {logs.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan="6">
                <p className="empty-table-message">{i18n("Text.NoLogFound")}</p>
              </Table.Cell>
            </Table.Row>
          )}
          {logs.map((log, l) => {
            const isExpanded = l === expandedRow;

            return (
              <React.Fragment key={`logRow${l}`}>
                <Table.Row>
                  <Table.Cell className="col-date">{log.date}</Table.Cell>
                  <Table.Cell className="col-subject">
                    <span>{log.subject || ""}</span>
                  </Table.Cell>
                  <Table.Cell className="col-sender">{log.sender}</Table.Cell>
                  <Table.Cell className="col-recipient">
                    {log.recipient}
                  </Table.Cell>
                  <Table.Cell className="col-status">
                    <span className={`log-status status-${log.status}`}>
                      <Tooltip
                        content={`${log.details}`}
                        id={`logRowDetails${l}`}
                      >
                        {log.status}
                      </Tooltip>
                    </span>
                  </Table.Cell>
                  <Table.Cell className="col-actions">
                    {isExpanded ? (
                      <ExpandLess
                        size="28"
                        fill="gray"
                        onClick={() => expandRow(null)}
                      />
                    ) : (
                      <ExpandMore
                        size="28"
                        fill="gray"
                        onClick={() => expandRow(l)}
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
                {isExpanded && (
                  <LogsTableActionsRow log={log} even={l % 2 === 0} />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </Panel>
  );
};

const LogsTableActionsRow = ({ even, log }) => {
  return (
    <>
      <Table.Row className={`col-actions-row ${even ? "gray" : "white"}`}>
        <Table.Cell colSpan={6}>
          <div className="log-row-data">
            <div className="subject">
              <h6>{i18n("Text.Subject")}</h6>
              <p>{log.subject}</p>
            </div>
            {!log.has_error && log.engagement && (
              <>
                <div className="engagement type-open">
                  <h6>{i18n("Text.EngagementOpened")}</h6>
                  {log.engagement.opened ? (
                    <p>
                      <span className="icon positive dashicons dashicons-yes-alt"></span>
                    </p>
                  ) : (
                    <p>
                      <span className="icon negative dashicons dashicons-no"></span>
                    </p>
                  )}
                </div>
                <div className="engagement type-clicked">
                  <h6>{i18n("Text.EngagementClicked")}</h6>
                  {log.engagement.clicked ? (
                    <p>
                      <span className="icon positive dashicons dashicons-yes-alt"></span>
                    </p>
                  ) : (
                    <p>
                      <span className="icon negative dashicons dashicons-no"></span>
                    </p>
                  )}
                </div>
              </>
            )}
            {log.has_error && (
              <div className="error-info">
                <h6>{i18n("Text.ErrorMessage")}</h6>
                <p>{log.details}</p>
              </div>
            )}
            {log.has_content && (
              <nav>
                <a href={log.preview_url} target="_blank">
                  {i18n("Text.ViewEmail")}
                </a>
              </nav>
            )}
          </div>
        </Table.Cell>
      </Table.Row>
      {/* The follow fake row is only here to avoid the background color changing (striped rows) */}
      <Table.Row className="col-actions-fake-row">
        <Table.Cell colSpan={6}></Table.Cell>
      </Table.Row>
    </>
  );
};

const LogsTablenavigator = ({
  currentPage,
  pagination,
  onPaginationChange,
}) => {
  if (pagination.total === 0) {
    return null;
  }

  return (
    <nav className="logs-table-navigator">
      {pagination.total && pagination.total > 0 && (
        <p className="total">
          {pagination.total} {i18n("Text.XEmailsFound")}
        </p>
      )}
      {pagination.max_pages && pagination.max_pages > 0 && (
        <Pagination
          currentPage={currentPage}
          pageRange={5}
          pages={pagination.max_pages}
          onChange={onPaginationChange}
        />
      )}
    </nav>
  );
};

export default hot(Logs);
